import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Checkbox,
  FormControlLabel,
  CircularProgress
} from "@material-ui/core";
import { Tab as TabIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import useSettings from "../../hooks/useSettings";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  sectionPaper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.1rem",
    },
  },
  sectionSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  settingItem: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.type === 'dark' ? '#555555' : '#e0e0e0'}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.type === 'dark' ? '#333333' : '#ffffff',
    transition: 'all 0.2s',
    marginBottom: theme.spacing(2),
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
    },
  },
  settingLabel: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  settingDescription: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginTop: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  loadingOverlay: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }
  },
  loadingSpinner: {
    marginLeft: theme.spacing(1),
  }
}));

export default function InterfaceSettings(props) {
  const { settings } = props;
  const classes = useStyles();
  const { update } = useSettings();

  // State for each setting
  const [showTabLabels, setShowTabLabels] = useState(true);
  const [showTabBadges, setShowTabBadges] = useState(true);
  const [showActionButtons, setShowActionButtons] = useState(true);
  const [enableFiltersSection, setEnableFiltersSection] = useState(true);
  
  // Loading states for each setting
  const [loading, setLoading] = useState({
    showTabLabels: false,
    showTabBadges: false,
    showActionButtons: false,
    enableFiltersSection: false,
  });

  // Load initial values from settings prop
  useEffect(() => {
    if (Array.isArray(settings) && settings.length > 0) {
      const showTabLabelsSetting = settings.find(s => s.key === "showTabLabels");
      const showTabBadgesSetting = settings.find(s => s.key === "showTabBadges");
      const showActionButtonsSetting = settings.find(s => s.key === "showActionButtons");
      const enableFiltersSectionSetting = settings.find(s => s.key === "enableFiltersSection");

      // Settings are stored as strings "true" or "false"
      setShowTabLabels(showTabLabelsSetting?.value !== "false");
      setShowTabBadges(showTabBadgesSetting?.value !== "false");
      setShowActionButtons(showActionButtonsSetting?.value !== "false");
      setEnableFiltersSection(enableFiltersSectionSetting?.value !== "false");
    }
  }, [settings]);

  // Handle toggle setting
  const handleToggleSetting = useCallback(async (key, currentValue, setter) => {
    const newValue = !currentValue;
    const stringValue = newValue ? "true" : "false";

    // Optimistically update UI
    setter(newValue);
    
    // Set loading state
    setLoading(prev => ({ ...prev, [key]: true }));

    try {
      console.log(`🔄 [InterfaceSettings] Updating ${key} to ${stringValue}`);
      
      const response = await update({ 
        key, 
        value: stringValue 
      });

      console.log(`✅ [InterfaceSettings] Successfully updated ${key}`, response);

      // Show success toast
      toastUtils.success(
        i18n.t("settings.settings.options.success"),
        i18n.t("settings.settings.options.updateSuccess")
      );

    } catch (error) {
      console.error(`❌ [InterfaceSettings] Error updating ${key}:`, error);

      // Revert UI state on failure
      setter(currentValue);

      // Show error toast
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          i18n.t("settings.settings.options.updateError");
      
      toastUtils.error(
        i18n.t("settings.settings.options.error"),
        errorMessage
      );
    } finally {
      // Clear loading state
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [update]);

  return (
    <div className={classes.container}>
      <Paper className={classes.sectionPaper}>
        <div className={classes.sectionHeader}>
          <TabIcon className={classes.sectionIcon} />
          <div>
            <Typography variant="h6" className={classes.sectionTitle}>
              {i18n.t("settings.settings.interface.title")}
            </Typography>
          </div>
        </div>
        <Typography className={classes.sectionSubtitle}>
          {i18n.t("settings.settings.interface.description")}
        </Typography>

        <Grid container spacing={2}>
          {/* Show Tab Labels */}
          <Grid item xs={12} md={6}>
            <Box className={classes.settingItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showTabLabels}
                    onChange={() => handleToggleSetting('showTabLabels', showTabLabels, setShowTabLabels)}
                    color="primary"
                    disabled={loading.showTabLabels}
                  />
                }
                label={
                  <Box>
                    <Typography className={classes.settingLabel}>
                      {i18n.t("settings.settings.interface.showTabLabels")}
                      {loading.showTabLabels && (
                        <CircularProgress size={16} className={classes.loadingSpinner} />
                      )}
                    </Typography>
                    <Typography className={classes.settingDescription}>
                      {i18n.t("settings.settings.interface.showTabLabelsDescription")}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Grid>

          {/* Show Tab Badges */}
          <Grid item xs={12} md={6}>
            <Box className={classes.settingItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showTabBadges}
                    onChange={() => handleToggleSetting('showTabBadges', showTabBadges, setShowTabBadges)}
                    color="primary"
                    disabled={loading.showTabBadges}
                  />
                }
                label={
                  <Box>
                    <Typography className={classes.settingLabel}>
                      {i18n.t("settings.settings.interface.showTabBadges")}
                      {loading.showTabBadges && (
                        <CircularProgress size={16} className={classes.loadingSpinner} />
                      )}
                    </Typography>
                    <Typography className={classes.settingDescription}>
                      {i18n.t("settings.settings.interface.showTabBadgesDescription")}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Grid>

          {/* Show Action Buttons */}
          <Grid item xs={12} md={6}>
            <Box className={classes.settingItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showActionButtons}
                    onChange={() => handleToggleSetting('showActionButtons', showActionButtons, setShowActionButtons)}
                    color="primary"
                    disabled={loading.showActionButtons}
                  />
                }
                label={
                  <Box>
                    <Typography className={classes.settingLabel}>
                      {i18n.t("settings.settings.interface.showActionButtons")}
                      {loading.showActionButtons && (
                        <CircularProgress size={16} className={classes.loadingSpinner} />
                      )}
                    </Typography>
                    <Typography className={classes.settingDescription}>
                      {i18n.t("settings.settings.interface.showActionButtonsDescription")}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Grid>

          {/* Enable Filters Section */}
          <Grid item xs={12} md={6}>
            <Box className={classes.settingItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enableFiltersSection}
                    onChange={() => handleToggleSetting('enableFiltersSection', enableFiltersSection, setEnableFiltersSection)}
                    color="primary"
                    disabled={loading.enableFiltersSection}
                  />
                }
                label={
                  <Box>
                    <Typography className={classes.settingLabel}>
                      {i18n.t("settings.settings.interface.enableFiltersSection")}
                      {loading.enableFiltersSection && (
                        <CircularProgress size={16} className={classes.loadingSpinner} />
                      )}
                    </Typography>
                    <Typography className={classes.settingDescription}>
                      {i18n.t("settings.settings.interface.enableFiltersSectionDescription")}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
}
